import { useCallback, useEffect, useState } from "react";
import {
  getStoredSession,
  storeSession,
  clearStoredSession,
  getStoredIdentity,
  storeIdentity,
  clearStoredIdentity,
  type CarrierSession,
} from "@/App/Auth/carrierSession";
import { exchangeLinkToken, fetchCarrierIdentity } from "@/App/Auth/carrierApi";

export type CarrierAuthState =
  | { status: "loading" }
  | { status: "signedOut" }
  /** Authorized by the link, but this browser hasn't said who its person is yet. */
  | { status: "needsIdentity" }
  | { status: "authed"; session: CarrierSession };

function readLinkToken(): string | undefined {
  return new URLSearchParams(window.location.search).get("token") ?? undefined;
}

/**
 * Resolve the carrier session on load:
 *  1. If the URL carries a signed link token, exchange it. A link is an explicit
 *     "sign in as this carrier", so it wins over any stored session — opening a
 *     different carrier's link switches carriers instead of being ignored.
 *     The exchange asserts the person's cached identity (name + work email);
 *     if this browser has no cached identity yet, we stop at `needsIdentity`
 *     and exchange only after `submitIdentity`.
 *  2. Otherwise, if a stored session works (/me succeeds), use it.
 *  3. Otherwise, the carrier is signed out (no link, no session).
 *
 * Because the link is bookmarked and permanent, step 1 transparently re-mints a
 * session on every visit, so an expired stored session is never a problem. The
 * identity is a per-browser, long-lived cache: the link is shared around the
 * carrier's team, so each browser identifies its person exactly once.
 */
export function useCarrierAuth(): {
  state: CarrierAuthState;
  /** From the needsIdentity screen: cache who this is and open the session. */
  submitIdentity: (name: string, email: string) => Promise<void>;
  /** Clears this browser's session + identity ("not you?"). */
  signOut: () => void;
} {
  const [state, setState] = useState<CarrierAuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    // oxlint-disable-next-line sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
    async function resolve(): Promise<void> {
      const linkToken = readLinkToken();
      if (linkToken) {
        const identity = getStoredIdentity();
        if (!identity) {
          if (!cancelled) setState({ status: "needsIdentity" });
          return;
        }
        try {
          const session = await exchangeLinkToken(linkToken, identity);
          storeSession(session);
          if (!cancelled) setState({ status: "authed", session });
          return;
        } catch {
          // A bad/expired link shouldn't strand a carrier who already has a
          // working session — fall through to the stored-session path.
        }
      }

      const stored = getStoredSession();
      if (stored) {
        try {
          const session = await fetchCarrierIdentity(stored.sessionToken);
          storeSession(session);
          if (!cancelled) setState({ status: "authed", session });
          return;
        } catch {
          clearStoredSession();
        }
      }

      if (!cancelled) setState({ status: "signedOut" });
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  const submitIdentity = useCallback(async (name: string, email: string) => {
    const linkToken = readLinkToken();
    if (!linkToken) {
      // The identity screen only shows when a link is present; if the token
      // somehow vanished (hand-edited URL), there is no way in.
      setState({ status: "signedOut" });
      return;
    }
    const identity = { name: name.trim(), email: email.trim() };
    // Let rejections propagate — the identity screen shows the error and the
    // person can correct their details.
    const session = await exchangeLinkToken(linkToken, identity);
    storeIdentity(identity);
    storeSession(session);
    setState({ status: "authed", session });
  }, []);

  const signOut = useCallback(() => {
    clearStoredSession();
    clearStoredIdentity();
    // The link in the URL still authorizes the carrier — sign-out just forgets
    // who this browser's person is, so the next person identifies themselves.
    setState(
      readLinkToken() ? { status: "needsIdentity" } : { status: "signedOut" },
    );
  }, []);

  return { state, submitIdentity, signOut };
}
